// Builds a fast id -> correctType map once, then fixes items in O(1) per side.
export function createInvalidLineTypeFixer(viewer, currentDataSource) {
  const layerRules = [
    { key: "lanemarker", type: "marker" },
    { key: "virtualline", type: "virtual" },
    { key: "turninglines", type: "turning" },
    { key: "turningarc", type: "turning" },
  ];

  function normalizeForCompare(str) {
    if (str === null || str === undefined) return "";
    return String(str).toLowerCase().replace(/\s+/g, "").replace(/[-_]+/g, "");
  }

  function deriveTypeFromLayerName(layerName) {
    const norm = normalizeForCompare(layerName);
    for (let i = 0; i < layerRules.length; i++) {
      if (norm.includes(layerRules[i].key)) return layerRules[i].type;
    }
    return null;
  }

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (!property) return undefined;
      return typeof property.getValue === "function" ? property.getValue() : property;
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (!property) return undefined;
      return typeof property.getValue === "function" ? property.getValue() : property;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object") return obj[key];
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

    return undefined;
  }

  function setPropertyValueOnProps(props, key, value) {
    if (!props) return false;

    // Cesium PropertyBag
    if (typeof props.get === "function") {
      const property = props.get(key);
      if (property && typeof property.setValue === "function") {
        property.setValue(value);
        return true;
      }
      if (typeof props.addProperty === "function") {
        props.addProperty(key, value);
        return true;
      }
    }

    // Internal bag
    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (property && typeof property.setValue === "function") {
        property.setValue(value);
        return true;
      }
      props._properties[key] = value;
      return true;
    }

    // Plain object
    props[key] = value;
    return true;
  }

  // Build id -> expectedType map once (fast path for fixes)
  const idToExpectedType = new Map();
  const dsCollection = viewer?.dataSources;

  if (dsCollection && typeof dsCollection.length === "number" && typeof dsCollection.get === "function") {
    for (let i = 0; i < dsCollection.length; i++) {
      const ds = dsCollection.get(i);
      if (!ds || ds === currentDataSource || !ds.entities) continue;

      const expectedType = deriveTypeFromLayerName(ds.layerName);
      if (!expectedType) continue;

      const entities = ds.entities.values || ds.entities;
      if (!Array.isArray(entities) || entities.length === 0) continue;

      for (let j = 0; j < entities.length; j++) {
        const e = entities[j];
        if (!e || !e.properties) continue;

        const idVal = getPropertyValueFromProps(e.properties, "id");
        if (idVal === null || idVal === undefined) continue;

        const k = String(idVal);
        if (!idToExpectedType.has(k)) idToExpectedType.set(k, expectedType);
      }
    }
  }

  // Fix one output item from getEntitiesWithInvalidLineTypes
  function fixItem(item) {
    const target = item?.entity;
    const props = target?.properties;
    if (!props) return { fixed: false, sides: [] };

    const sides = [];

    // Left
    const leftId = getPropertyValueFromProps(props, "left_line_id");
    if (leftId !== null && leftId !== undefined) {
      const expected = idToExpectedType.get(String(leftId));
      if (expected) {
        const cur = getPropertyValueFromProps(props, "left_line_type");
        if (normalizeForCompare(cur) !== normalizeForCompare(expected)) {
          setPropertyValueOnProps(props, "left_line_type", expected);
          sides.push("left");
        }
      }
    }

    // Right
    const rightId = getPropertyValueFromProps(props, "right_line_id");
    if (rightId !== null && rightId !== undefined) {
      const expected = idToExpectedType.get(String(rightId));
      if (expected) {
        const cur = getPropertyValueFromProps(props, "right_line_type");
        if (normalizeForCompare(cur) !== normalizeForCompare(expected)) {
          setPropertyValueOnProps(props, "right_line_type", expected);
          sides.push("right");
        }
      }
    }

    return { fixed: sides.length > 0, sides };
  }

  // Fix whole array output (mutates entities in-place)
  function fixAll(items) {
    if (!Array.isArray(items) || items.length === 0) return [];
    const report = new Array(items.length);
    for (let i = 0; i < items.length; i++) report[i] = fixItem(items[i]);
    return report;
  }

  return { fixItem, fixAll, idToExpectedType };
}

// Convenience helper
export function fixInvalidLineTypes(viewer, currentDataSource, invalidItems) {
  const fixer = createInvalidLineTypeFixer(viewer, currentDataSource);
  return fixer.fixAll(invalidItems);
}
